import * as cx from 'classnames';
import {
    AUTHOR,
    COMMENT_DELETED_BECAUSE_reason,
    COMMENT_DELETED_BY_ADMIN,
    COMMENT_DELETED_BY_AUTHOR,
    FORUM_CANT_SEE_ANSWER,
} from 'mk/autogenerated/translations/CommentBody.7f6d2793b7f84bf6d35f3f282b1b884e'
import { getInlinedImages, linebreaksbr, mentionize, replaceImageMarks, urlize } from 'mk/bazaar/common/filters';
import { hashize } from 'mk/bazaar/common/filters/hashize';
import assetUrl from 'mk/common/assetUrl';
import { photoUrl } from 'mk/photo/photoUrl';
import { group_feed_url, photoblog_index_url, user_profile_url } from 'mk/urls/functions';
import { CommentEntity } from 'mk2/apps/blogs/schemas';
import { largestAvailablePhotoVersionOfPost, linkize } from 'mk2/apps/forum/utils';
import AnnouncementBox, { BoxType } from 'mk2/components/AnnouncementBox';
import Emojify from 'mk2/components/Emojify';
import Img from 'mk2/components/Img';
import * as React from 'react';
import styles from './CommentBody.mscss';

interface OwnProps {
    comment: CommentEntity;
    isCollapsed: boolean;
    inForum: boolean;
    inGroups: boolean;
    followLinks: boolean;
    location?: string;

    hasHiddenText: boolean;
    hasDeletedStatus: boolean;
}

type Props = OwnProps;

export default class CommentBody extends React.Component<Props> {

    public render() {
        const {
            comment, followLinks, hasDeletedStatus, isCollapsed, hasHiddenText, inForum, inGroups, location,
        } = this.props;

        const inBlogs = !(inForum || inGroups);

        // FIXME cemi to be implemented in filters.ts
        const unhtml = (s: any) => s;
        const cleanText = unhtml(comment.text);

        const inlinedImages = inForum ? getInlinedImages(cleanText) : [];
        const belowImages = comment.photos ? comment.photos.filter((photo, idx) => !(idx in inlinedImages)) : [];

        // prepare comment message body
        let bodyChunks: React.ReactChild[] = [cleanText];
        if (comment.text) {
            bodyChunks = linebreaksbr(mentionize(urlize(linkize(bodyChunks), 50, {nofollow: !followLinks})));
            if (!inForum && location) {
                const path = location.split('/')[1];
                const groupId = path === 'group' ? location.split('/')[2] : null;
                bodyChunks = hashize(bodyChunks, (hash) => groupId ? group_feed_url(groupId, { h: hash }) : photoblog_index_url({ h: hash }));
            }
            bodyChunks = inlinedImages
                ? replaceImageMarks(
                    bodyChunks, comment.photos, styles.CommentBody__photoImg,
                    (photo) => largestAvailablePhotoVersionOfPost(photo, 's600x600'),
                )
                : bodyChunks;
        }

        const isPostAuthor = comment.authorUser.username === comment.targetOwner.username;
        const showDeletedStatus = hasDeletedStatus && comment.deletedAt && !isCollapsed;

        return (
            // admins are using strange JS code bookmarklet to fetch usernames
            <div className={styles.CommentBody}>
                {showDeletedStatus && (
                    <AnnouncementBox
                        message={comment.deletedByAuthor ? COMMENT_DELETED_BY_AUTHOR : COMMENT_DELETED_BY_ADMIN}
                        deletedBy={comment.deletedBy ? comment.deletedBy.username : null}
                        detailMessage={comment.deletedReason ? comment.deletedReason : null}
                        deletedAt={comment.deletedAt}
                        hasDetailMessage={!!comment.deletedReason}
                        icon={'fa fa-trash'}
                        reasonValue={COMMENT_DELETED_BECAUSE_reason}
                        type={BoxType.DeletedMessage}
                    />)
                }

                <div className={cx({'comment2-main' : !inForum})}>
                    {!hasHiddenText ? [].concat(...[
                        (!!comment.text || !!comment.stickerId || (belowImages.length !== 0)) && (!comment.deletedAt || (comment.deletedAt && !isCollapsed)) && [
                            <div
                                key="text"
                                className={cx(
                                    styles.CommentBody__text,
                                    inBlogs && styles['CommentBody__text--inBlogs'],
                                    inGroups && styles['CommentBody__text--inGroups'],
                                )}
                            >
                                {inBlogs && (
                                    // admins are using strange JS code bookmarklet to fetch usernames
                                    <a
                                        className={cx(styles.CommentBody__authorBlogs, 'comment2-profile-link')}
                                        href={user_profile_url(comment.authorUser.username)}
                                    >
                                        {comment.authorUser.username}
                                    </a>
                                )}
                                {inBlogs && isPostAuthor && (
                                    <span className={styles.CommentBody__authorBadgeBlogs}>{AUTHOR}</span>
                                )}

                                <Emojify size={17} force={inBlogs}>
                                    {bodyChunks}
                                </Emojify>
                            </div>,
                        ],
                        !!comment.stickerId && [
                            <Img
                                className={cx(
                                    styles.CommentBody__sticker,
                                    inBlogs && styles['CommentBody__sticker--inBlogs'],
                                )}
                                key="stickerImg"
                                src={assetUrl + 'img/photoblog/stickers/' + comment.stickerId + '.png'}
                                width={75}
                                height={75}
                            />,
                        ],
                        ((!comment.deletedAt && belowImages.length !== 0) || (showDeletedStatus && belowImages.length !== 0)) && [
                            <div
                                key="photos"
                                className={cx(
                                    styles.CommentBody__photos,
                                    inBlogs && styles['CommentBody__photoImg--inBlogs'],
                                )}
                            >
                                {belowImages.map((photo, idx) => (
                                    <a
                                        className={cx(styles.CommentBody__photo, idx === 0 && styles['CommentBody__photo--isFirst'])}
                                        target="_blank"
                                        key={photo.id}
                                        href={photoUrl(
                                            photo,
                                            inForum
                                                ? largestAvailablePhotoVersionOfPost(photo, 's1600x1600')
                                                : 's1600x1600',
                                        )}
                                    >
                                        <Img
                                            className={styles.CommentBody__photoImg}
                                            src={photoUrl(photo, inForum
                                                    ? largestAvailablePhotoVersionOfPost(photo, 's600x600')
                                                    : 's305x150',
                                            )}
                                            height={photo.height}
                                            width={photo.width}
                                        />
                                    </a>
                                ))}
                            </div>,
                        ],
                    ]) : (!comment.deletedAt && (
                        <div className={styles.CommentBody__hiddenText}>
                            {FORUM_CANT_SEE_ANSWER}
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}
